type CookieCategories = {
  analytics: boolean;
  advertisement: boolean;
  functional: boolean;
  personalization: boolean;
};

const DEFAULT_CATEGORIES: CookieCategories = {
  analytics: false,
  advertisement: false,
  functional: false,
  personalization: false,
};

function parseCookieYesConsentCookie(): Partial<CookieCategories> {
  if (typeof document === "undefined") return {};
  const entry = document.cookie
    .split(";")
    .map((chunk) => chunk.trim())
    .find((chunk) => chunk.startsWith("cookieyes-consent="));

  if (!entry) return {};
  const raw = decodeURIComponent(entry.split("=")[1] || "");
  const pairs = raw.split(",").map((pair) => pair.trim());
  const map = new Map<string, string>();
  for (const pair of pairs) {
    const [key, value] = pair.split(":");
    if (key && value) map.set(key, value);
  }

  return {
    analytics: map.get("analytics") === "yes",
    advertisement: map.get("advertisement") === "yes",
    functional: map.get("functional") === "yes" || map.get("performance") === "yes",
    personalization: map.get("functional") === "yes" || map.get("other") === "yes",
  };
}

export function getConsentCategories(): CookieCategories {
  const fromApi = window.getCkyConsent?.();
  if (fromApi?.categories) {
    return {
      analytics: Boolean(fromApi.categories.analytics),
      advertisement: Boolean(fromApi.categories.advertisement),
      functional:
        Boolean(fromApi.categories.functional) || Boolean(fromApi.categories.performance),
      personalization: Boolean(fromApi.categories.functional),
    };
  }

  return { ...DEFAULT_CATEGORIES, ...parseCookieYesConsentCookie() };
}

export function syncGoogleConsentMode(): void {
  if (typeof window.gtag !== "function") return;
  const categories = getConsentCategories();
  window.gtag("consent", "update", {
    ad_storage: categories.advertisement ? "granted" : "denied",
    ad_user_data: categories.advertisement ? "granted" : "denied",
    ad_personalization: categories.advertisement ? "granted" : "denied",
    analytics_storage: categories.analytics ? "granted" : "denied",
    functionality_storage: categories.functional ? "granted" : "denied",
    personalization_storage: categories.personalization ? "granted" : "denied",
    security_storage: "granted",
  });
}

export function hasAnalyticsConsent(): boolean {
  return getConsentCategories().analytics;
}

export function hasAdvertisementConsent(): boolean {
  return getConsentCategories().advertisement;
}

export function initCookieConsentSync(): () => void {
  const pushConsentState = () => {
    const categories = getConsentCategories();
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "cookie_consent_update",
      consent_analytics: categories.analytics,
      consent_advertisement: categories.advertisement,
      consent_functional: categories.functional,
      consent_personalization: categories.personalization,
    });
    syncGoogleConsentMode();
  };

  const onBannerLoad = () => pushConsentState();
  const onConsentUpdate = () => pushConsentState();

  document.addEventListener("cookieyes_banner_load", onBannerLoad as EventListener);
  document.addEventListener("cookieyes_consent_update", onConsentUpdate as EventListener);
  pushConsentState();

  return () => {
    document.removeEventListener("cookieyes_banner_load", onBannerLoad as EventListener);
    document.removeEventListener(
      "cookieyes_consent_update",
      onConsentUpdate as EventListener
    );
  };
}
